/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI, PublicPluginAPI } from '@atlaskit/editor-common/types';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { openMauiEmbedSpacePickerCommand } from '../../editor-commands/maui-embed-space-picker';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { parseMauiDiagramLink, WHITEBOARD_PATH_REGEX } from '../../pm-plugins/detect-maui-link';
import { MauiEmbedFinalizeStatus } from '../../pm-plugins/maui-diagram-paste-plugin';

const MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID = 'maui-whiteboard';
// Gives fast finalize/URL replacement a chance to settle before showing a false warning.
const NEEDS_FINALIZE_BANNER_DELAY_MS = 2 * 60 * 1000;

const messages = defineMessages({
	bannerNeedsFinalizeMessage: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.bannerNeedsFinalizeMessage',
		defaultMessage:
			'Diagram not visible to others. Move your diagram’s whiteboard to make it visible to others and avoid auto removal.',
		description:
			'Banner copy shown when a MAUI whiteboard is still parented in a Rovo system space and needs to be moved so other people can view it and it is not automatically removed.',
	},
	bannerFinalizingMessage: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.bannerFinalizingMessage',
		defaultMessage: 'Moving whiteboard…',
		description:
			'Banner copy shown while the MAUI diagram whiteboard is being moved to the selected location.',
	},
	bannerFailedMessage: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.bannerFailedMessage',
		defaultMessage: "We couldn't move this diagram to this page. Please try again.",
		description: 'Banner copy shown when moving the MAUI whiteboard to the current space failed.',
	},
	moveToPageButton: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.moveToPageButton',
		defaultMessage: 'Move',
		description:
			'Primary button label that starts moving the MAUI diagram whiteboard to a location where other people can view it.',
	},
	movingButton: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.movingButton',
		defaultMessage: 'Moving…',
		description: 'Primary button label while the MAUI diagram move is in flight.',
	},
	tryAgainButton: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.tryAgainButton',
		defaultMessage: 'Try again',
		description: 'Primary button label shown after a failed MAUI diagram move attempt.',
	},
});

const styles = cssMap({
	banner: {
		position: 'absolute',
		top: token('space.0'),
		left: token('space.0'),
		right: token('space.0'),
		// `card` z-index keeps this above embed chrome but below modals/flags.
		zIndex: 100,
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
		backgroundColor: token('color.background.warning'),
		color: token('color.text.warning'),
		borderBottom: `${token('border.width')} solid ${token('color.border.warning')}`,
	},
	bannerContent: {
		width: '100%',
	},
	bannerMessage: {
		font: token('font.body.small'),
	},
	actionLink: {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.link'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
		'&:disabled': {
			color: token('color.text.disabled'),
			textDecoration: 'none',
			cursor: 'progress',
		},
	},
});

type NativeEmbedOverlayRenderProps = {
	experienceId?: string;
	localId?: string;
	resolvedMeta?: unknown;
	url?: string;
};

type Props = NativeEmbedOverlayRenderProps & {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
};

const parseWhiteboardContentId = (url: string | undefined): string | undefined => {
	if (!url) {
		return undefined;
	}
	return WHITEBOARD_PATH_REGEX.exec(url)?.[2];
};

const shouldShowBanner = (status: MauiEmbedFinalizeStatus) =>
	status === MauiEmbedFinalizeStatus.NEEDS_FINALIZE ||
	status === MauiEmbedFinalizeStatus.FINALIZING ||
	status === MauiEmbedFinalizeStatus.FAILED ||
	(status === MauiEmbedFinalizeStatus.NEEDS_SPACE_SELECTION &&
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
		!FeatureGates.getExperimentValue<boolean>(
			'maui-native-embed-finalize-diagram-anywhere',
			'value',
			false,
		));

const TEST_ID = 'native-embed-extension--rovo-system-space-banner';

export const MauiNativeEmbedBanner = ({
	api,
	experienceId,
	url,
}: Props): React.JSX.Element | null => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isNeedsFinalizeBannerDelayElapsed, setIsNeedsFinalizeBannerDelayElapsed] = useState(false);
	const probedKeyRef = useRef<string | null>(null);
	// Tracks whether the banner is still mounted so we don't call
	// `setIsSubmitting(false)` after the component has been unmounted —
	// which happens when the in-flight finalize causes status to reach
	// FINALIZED and `shouldShowBanner` returns false.
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const isMauiWhiteboard = experienceId === MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID;
	const contentId = useMemo(
		() => (isMauiWhiteboard ? parseWhiteboardContentId(url) : undefined),
		[isMauiWhiteboard, url],
	);

	// If the embed URL carries an `_rc` conversation-context payload (e.g.
	// the original Rovo Chat link), forward it so `finalizeAfterMove` can
	// take the convo-ai path. When absent (e.g. older links, or links
	// already moved/cleaned), the consumer falls back to the monolith
	// finalize endpoint.
	const conversationContext = useMemo(
		() => (isMauiWhiteboard && url ? parseMauiDiagramLink(url)?.conversationContext : undefined),
		[isMauiWhiteboard, url],
	);
	const status = useSharedPluginStateWithSelector(api, ['malleableUi'], (states) => {
		const entry = contentId
			? states.malleableUiState?.diagramPaste?.embedFinalizeStates?.[contentId]
			: undefined;
		return (
			(entry?.status as MauiEmbedFinalizeStatus | undefined) ?? MauiEmbedFinalizeStatus.UNKNOWN
		);
	});
	const finalizeAction = api?.malleableUi?.actions?.finalizeMauiEmbedFromIndicator;

	// When on, rolls back to the 2-minute warning delay; when off, finalize status alone drives visibility.
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
	const isStreamingWarningKillswitchOn = FeatureGates.getExperimentValue<boolean>(
		'confluence_diagram_native_embed_warning_streaming',
		'value',
		false,
	);

	useEffect(() => {
		if (!isStreamingWarningKillswitchOn) {
			setIsNeedsFinalizeBannerDelayElapsed(true);
			return;
		}

		if (
			!isMauiWhiteboard ||
			!contentId ||
			!url ||
			status !== MauiEmbedFinalizeStatus.NEEDS_FINALIZE
		) {
			setIsNeedsFinalizeBannerDelayElapsed(false);
			return;
		}

		setIsNeedsFinalizeBannerDelayElapsed(false);
		const timerId = setTimeout(() => {
			setIsNeedsFinalizeBannerDelayElapsed(true);
		}, NEEDS_FINALIZE_BANNER_DELAY_MS);

		return () => {
			clearTimeout(timerId);
		};
	}, [contentId, isMauiWhiteboard, isStreamingWarningKillswitchOn, status, url]);

	// Fallback probe for pre-existing un-moved diagrams; self-skips once status is set (e.g. CHECKING during streaming).
	useEffect(() => {
		if (!isMauiWhiteboard || !contentId || !url || !finalizeAction) {
			return;
		}
		const key = `${contentId}::${url}`;
		if (probedKeyRef.current === key) {
			return;
		}
		if (status !== MauiEmbedFinalizeStatus.UNKNOWN) {
			probedKeyRef.current = key;
			return;
		}
		probedKeyRef.current = key;
		createAnalyticsEvent?.({
			action: 'probed',
			actionSubject: 'mauiDiagram',
			actionSubjectId: 'embedFinalize',
			eventType: EVENT_TYPE.OPERATIONAL,
			attributes: { contentId },
		})?.fire(FabricChannel.editor);
		void finalizeAction({
			contentId,
			originalUrl: url,
			conversationContext,
			mode: 'probe',
		});
	}, [
		contentId,
		conversationContext,
		createAnalyticsEvent,
		finalizeAction,
		isMauiWhiteboard,
		status,
		url,
	]);

	const onMoveToPage = useCallback(async () => {
		if (!contentId || !url || isSubmitting) {
			return;
		}
		// If the probe already denied permission, re-open the picker instead of
		// re-running the probe (which would just deny again).
		if (
			status === MauiEmbedFinalizeStatus.NEEDS_SPACE_SELECTION &&
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
			!FeatureGates.getExperimentValue<boolean>(
				'maui-native-embed-finalize-diagram-anywhere',
				'value',
				false,
			)
		) {
			api?.core?.actions.execute(openMauiEmbedSpacePickerCommand({ contentId, originalUrl: url }));
			return;
		}
		if (!finalizeAction) {
			return;
		}
		setIsSubmitting(true);
		try {
			await finalizeAction({
				contentId,
				originalUrl: url,
				conversationContext,
				mode: 'finalize',
			});
		} finally {
			if (mountedRef.current) {
				setIsSubmitting(false);
			}
		}
	}, [contentId, conversationContext, api, finalizeAction, isSubmitting, status, url]);

	if (
		!isMauiWhiteboard ||
		!shouldShowBanner(status) ||
		// Killswitch on: suppress the warning during the grace period (no-op when off).
		(status === MauiEmbedFinalizeStatus.NEEDS_FINALIZE && !isNeedsFinalizeBannerDelayElapsed)
	) {
		return null;
	}

	const inFlight = isSubmitting || status === MauiEmbedFinalizeStatus.FINALIZING;
	const isFailed = status === MauiEmbedFinalizeStatus.FAILED;

	const messageId = inFlight
		? messages.bannerFinalizingMessage
		: isFailed
			? messages.bannerFailedMessage
			: messages.bannerNeedsFinalizeMessage;

	const buttonLabelId = inFlight
		? messages.movingButton
		: isFailed
			? messages.tryAgainButton
			: messages.moveToPageButton;

	return (
		<Box xcss={styles.banner} testId={`${TEST_ID}--wrapper`} role="status" aria-live="polite">
			<Inline xcss={styles.bannerContent} space="space.075" alignBlock="center">
				<WarningIcon label="" color="currentColor" spacing="spacious" testId={`${TEST_ID}--icon`} />
				<Inline space="space.075" alignBlock="center" xcss={styles.bannerMessage}>
					<Box testId={`${TEST_ID}--message`}>{intl.formatMessage(messageId)}</Box>
					<Pressable
						xcss={styles.actionLink}
						isDisabled={inFlight}
						onClick={onMoveToPage}
						testId={`${TEST_ID}--action-button`}
					>
						{intl.formatMessage(buttonLabelId)}
					</Pressable>
				</Inline>
			</Inline>
		</Box>
	);
};

type MauiNativeEmbedBannerEditorAPI = PublicPluginAPI<[MalleableUiPlugin]>;

export const createMauiNativeEmbedBannerRenderer =
	(
		api?: MauiNativeEmbedBannerEditorAPI,
	): ((props: NativeEmbedOverlayRenderProps) => React.JSX.Element) =>
	(props: NativeEmbedOverlayRenderProps) => (
		<MauiNativeEmbedBanner
			{...props}
			api={api as ExtractInjectionAPI<MalleableUiPlugin> | undefined}
		/>
	);
